// import useLocalStorage from "app/core/hooks/useLocalStorage"
import { useParam } from "@blitzjs/next"
import { rgba2hex } from "app/core/components/devices/IOS"
import useLocalStorage from "app/core/hooks/useLocalStorage"
import usePrevious from "app/core/hooks/usePrevious"
import { useSpecificPaywallContext } from "app/paywalls/components/forms/editorV3/contexts/PaywallContext"
import { loadTemplate } from "app/paywalls/helpers/templateJsonSchemaMigrations"
import {
  PaywallTemplate,
  PaywallTemplateLatestVersion,
  TPaywallTemplate,
} from "app/schema/paywallTemplate/paywallTemplate"
import _ from "lodash"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useField, useFormState } from "react-final-form"
import useBuilderPostMessage from "../../hooks/useBuilderPostMessage"
import { defaultJson } from "./utils"

type TPaywallBuilder = {
  children?: ReactNode
  json?: string
  setJson: (
    // TODO: Update params to take in single object with rest as properties
    json: string | undefined,
    resetJson?: boolean,
    ignoreHistory?: boolean,
    ignoreSubMigration?: boolean
  ) => void
  parsedJson: TPaywallTemplate
  jsonError?: string
  onUndo: () => void
  onRedo: () => void
  onClearTemplate: () => void
}

type THistory = {
  index: number | null // current index of history state
  states: string[] // array of history states (parsed paywalls)
}

export const TemplateJsonContext = createContext<TPaywallBuilder>({
  json: undefined,
  setJson: () => {},
  parsedJson: { version: PaywallTemplateLatestVersion },
  jsonError: undefined,
  onUndo: () => {},
  onRedo: () => {},
  onClearTemplate: () => {},
})

export const initialJson = JSON.stringify({ ...defaultJson }, null, 2)

const PAYWALL_TEMPLATE_MAX_VERSION = 11

export const TemplateJsonProvider = ({ children }) => {
  // The form state is used to set the initial template json data.
  // The PaywallEditor handles what initial data should be set
  // using the PaywallStateController, which checkes for local storage data
  // for the current paywall. Whatever it uses should be the data that this
  // provider should use.
  const formState = useFormState()
  const { values } = formState
  const { templateJson: initialTemplateJson } = values
  // ----------------------------------------------------------------------

  // This keeps track of the localStorage `paywall_state` data
  const [paywallState, setPaywallState] = useLocalStorage("paywall_state", "[]")
  const parsedPaywallState = useMemo(() => {
    let parsed
    try {
      parsed = JSON.parse(paywallState)
    } catch (e) {
      parsed = []
      console.log("error parsing local storage state", e)
    }
    return parsed
  }, [paywallState])
  // ---------------------------------------------------------

  const paywallId = useParam("paywallId", "number")!
  const isMounted = useRef(false)
  const allowStoringLocalJson = useRef(false)
  const previousIsMounted = usePrevious(isMounted.current)
  const { iframeRef, setIsInitializing } = useSpecificPaywallContext({
    iframeRef: true,
    setIsInitializing: true,
  })
  const {
    input: { value: loadingBackgroundColor, onChange: onChangeBackgroundColor },
  } = useField("backgroundColorHex")

  const {
    input: { value: substitutions, onChange: onSubstitutionsChange },
  } = useField("substitutions")

  /**
   * Keep this here for now. We may want to use this when storing unsaved editor states.
   */
  // const [localStorageJson, setLocalStorageJson] = useLocalStorage("paywall_template_json", "")

  const [json, setJsonState] = useState(initialJson)
  const [parsedJson, setParsedJson] = useState<TPaywallTemplate>(
    (initialTemplateJson as TPaywallTemplate) || defaultJson
  )
  const [jsonError, setJsonError] = useState<string | undefined>()

  const { sendTemplateData } = useBuilderPostMessage(iframeRef)

  // Session history
  const [history, setHistory] = useState<THistory>({
    index: null,
    states: [],
  })
  const previousJson = usePrevious(json)
  const isHistoryAtLatestState = useMemo(() => {
    return (
      history.index === history.states.length - 1 ||
      (history.index === null && history.states.length === 0)
    )
  }, [history])

  useEffect(() => {
    if (json && !previousJson) {
      setHistory({
        index: 0,
        states: [json],
      })
    }
  }, [json, previousJson, setHistory])

  // This keeps the loading paywall objects `backgroundColorHex` property
  // in sync with the template json `pageSettings.paywallBackgroundColor` property
  useEffect(() => {
    let bgHex = rgba2hex(parsedJson?.pageSettings?.paywallBackgroundColor)
    bgHex = bgHex?.replace("#", "")
    bgHex = `#${bgHex?.slice(0, 6)}`
    if (bgHex && bgHex !== loadingBackgroundColor) {
      onChangeBackgroundColor(bgHex)
    }
  }, [parsedJson, loadingBackgroundColor, onChangeBackgroundColor])

  // Hack to prevent storing json to local storage on initial load
  useEffect(() => {
    if (!allowStoringLocalJson.current) {
      setTimeout(() => {
        allowStoringLocalJson.current = true
        setIsInitializing(false)
      }, 1000)
    }
  }, [setIsInitializing])

  const updateTemplateJson = useCallback(
    (newJson) => {
      // Hack to prevent storing json to local storage on initial load
      if (!allowStoringLocalJson.current) return

      const thisLocalPaywall = parsedPaywallState.find((p) => p.id === paywallId)
      let newState = parsedPaywallState

      if (!thisLocalPaywall) {
        newState = [
          ...parsedPaywallState,
          {
            ...values,
            templateJson: newJson,
          },
        ]
      } else {
        newState = _.map(parsedPaywallState, (p) => {
          if (p.id !== paywallId) return p

          return {
            ...p,
            templateJson: newJson,
          }
        })
      }
      setPaywallState(JSON.stringify(newState))
    },
    [paywallId, parsedPaywallState, setPaywallState, values]
  )

  // Sets history state
  // This wil either append a new history state to the end of the history array
  // or it will alter the history array and set the last history item to
  // whatever the current json state is.
  const onSetHistory = useCallback(
    (json: string) => {
      if (isHistoryAtLatestState) {
        setHistory({
          index: history.states.length,
          states: [...history.states, json],
        })
      } else {
        setHistory({
          index: history?.index! + 1,
          states: [...history.states.slice(0, history?.index! + 1), json],
        })
      }
    },
    [history, isHistoryAtLatestState, setHistory]
  )

  const {
    input: { onChange: onChangeTemplateJsonValue },
  } = useField("templateJson")

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
    }
  }, [])

  // This ensures that the initial template json data is set
  // in paywall-ui by sending the post message data
  useEffect(() => {
    if (previousIsMounted === false && isMounted.current === true) {
      let migratedJson = initialTemplateJson
      if (initialTemplateJson?.version !== PaywallTemplateLatestVersion) {
        const { templateJson: newTemplateJson } = loadTemplate(
          initialTemplateJson,
          substitutions,
          PAYWALL_TEMPLATE_MAX_VERSION
        )
        migratedJson = newTemplateJson
      }

      try {
        PaywallTemplate.parse(migratedJson)
        // TODO: Update this to event-based mechanism instead of setTimeout
        // Using a setTimeout here because without it we may send the postMessage to paywall-ui
        // before paywall-ui is ready to receive postMessages.
        setTimeout(() => {
          sendTemplateData(migratedJson, false)
        }, 400)
      } catch (e) {
        console.log("json schema validation error", e)
      }
    }
  }, [initialTemplateJson, sendTemplateData, previousIsMounted, substitutions])

  const setJson = useCallback(
    (json, resetJson = false, ignoreHistory = false, ignoreSubMigration = false) => {
      setJsonState(json)

      let parsed: TPaywallTemplate
      setJsonError(undefined)

      try {
        parsed = JSON.parse(json)
      } catch (e) {
        console.log("Error parsing JSON", e)
        setJsonError("JSON format error")
        return
      }

      try {
        let migratedJson = parsed
        let migratedSubs = substitutions
        if (parsed?.version !== PaywallTemplateLatestVersion) {
          const { templateJson: newTemplateJson, substitutions: newSubs } = loadTemplate(
            parsed,
            substitutions,
            PAYWALL_TEMPLATE_MAX_VERSION
          )
          migratedJson = newTemplateJson
          migratedSubs = newSubs
        }

        PaywallTemplate.parse(migratedJson)

        migratedSubs && !ignoreSubMigration && onSubstitutionsChange(migratedSubs)

        sendTemplateData(migratedJson, resetJson)
        setParsedJson(migratedJson!)
        // setLocalStorageJson(resetJson ? "" : JSON.stringify(migratedJson))
        onChangeTemplateJsonValue(migratedJson)
        !ignoreHistory && onSetHistory(json)
        isMounted && updateTemplateJson(migratedJson)
      } catch (e) {
        console.log("json schema validation error", e)
        setJsonError("JSON schema validation error. Check console for more info.")
      }
    },
    [
      sendTemplateData,
      onChangeTemplateJsonValue,
      onSetHistory,
      updateTemplateJson,
      substitutions,
      onSubstitutionsChange,
    ]
  )

  // Undo/Redo history state
  const onUndo = useCallback(() => {
    if (history.index !== null && history.index > 0) {
      setHistory({
        index: history.index - 1,
        states: history.states,
      })
      setJson(history.states[history.index - 1], false, true)
    }
  }, [history, setHistory, setJson])

  const onRedo = useCallback(() => {
    if (history.index !== null && history?.index < history.states.length - 1) {
      setHistory({
        index: history.index + 1,
        states: history.states,
      })
      setJson(history.states[history.index + 1], false, true)
    }
  }, [history, setHistory, setJson])

  const onClearTemplate = useCallback(() => {
    setJson(
      JSON.stringify({ ...parsedJson, backgroundContent: [], mainContent: [], cardContent: [] })
    )
  }, [parsedJson, setJson])

  const value = useMemo(
    () => ({ json, setJson, jsonError, parsedJson, onUndo, onRedo, onClearTemplate }),
    [json, setJson, jsonError, parsedJson, onUndo, onRedo, onClearTemplate]
  )

  return <TemplateJsonContext.Provider value={value}>{children}</TemplateJsonContext.Provider>
}

export const useTemplateJsonContext = () => useContext(TemplateJsonContext)
